import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';
import { Availability, AvailabilityTypeId, Gather, JobPosting, JobSearchStatus, JobSearchStatusEnum } from '../../gather-typings';
import { GatherApiService } from '../../gather-services/gather-api.service';
import { GatherStateService } from '../../gather-services/gather-state.service';
import { SharedDataService } from '../../gather-services/shared-data.service';
import { NotInterestedReasons } from '../../enums';

@Component({
  selector: 'not-a-fit-modal',
  templateUrl: 'not-a-fit-modal.component.html',
  imports: [
    TranslateModule,
    MatRadioModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIcon
  ],
  styleUrls: ['./not-a-fit-modal.component.scss']
})

export class NotAFitModalComponent implements OnInit {

  constructor(
    public dialogRef: MatDialogRef<NotAFitModalComponent>,
    private gatherStateService: GatherStateService,
    private gatherApiService: GatherApiService,
    private sharedDataService: SharedDataService,
    private router: Router,
    private route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
  ) {}

  trackingId: string = null;
  jobPosting: JobPosting;
  gather: Gather;
  fromTextMessage: boolean;
  jobSearchStatus: JobSearchStatus;
  formGroup: UntypedFormGroup;
  encounteredError: boolean;
  availability: Availability;
  readonly notInterestedReasons: string[] = Object.keys(this.translateService.instant('gather.not-interested.reason') as object);

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.fromTextMessage =
      this.route.snapshot.queryParamMap.has('textMessage') && this.route.snapshot.queryParamMap.get('textMessage') === 'true';
    // set gather subscription
    this.subscriptions.add(
      this.sharedDataService.gather.subscribe(gather => {
        this.gather = gather;
      })
    );
    // set job posting
    this.subscriptions.add(
      this.sharedDataService.jobPosting.subscribe(posting => {
        this.jobPosting = posting;
      })
    );
    // set email_tracking_id uuid
    this.subscriptions.add(
      this.sharedDataService.trackingId.subscribe(id => {
        this.trackingId = id;
        this.gatherApiService.updateGatherResponseNotInterested(this.trackingId, this.fromTextMessage);

        this.subscriptions.add(
          this.gatherApiService.getMoatsAvailability(this.trackingId).subscribe(availability => {
            this.availability = availability;
          })
        );
      })
    );

    // set the title
    this.gatherStateService.setTitle('gather.not-interested.title');

    this.initializeFormGroup();
  }

  initializeFormGroup(): void {
    this.formGroup = this.formBuilder.group({
      notInterestedReasons: new UntypedFormGroup({
        skillSetMismatch: new UntypedFormControl(false),
        levelMismatch: new UntypedFormControl(false),
        payRate: new UntypedFormControl(false),
        location: new UntypedFormControl(false),
        industry: new UntypedFormControl(false),
        notActivelyLooking: new UntypedFormControl(false),
        other: new UntypedFormControl(false)
      }),
      notInterestedNotes: new UntypedFormControl('')
    });
  }

  convertToCamelCase(str: string): string {
    return str.replace(/-([a-z])/g, group => {
      return group[1].toUpperCase();
    });
  }

  isSubmitDisabled(): boolean {
    const formControlKeys = Object.keys(this.formGroup['controls'].notInterestedReasons['controls'] as object);
    let anyCheckboxChecked = false;
    for (const formControlKey of formControlKeys) {
      if (this.formGroup.get('notInterestedReasons').get(formControlKey).value) {
        anyCheckboxChecked = true;
        break;
      }
    }

    return !anyCheckboxChecked;
  }

  saveAsNotInterested(): void {
    this.setJobSearchStatus();

    this.gatherApiService
      .saveAsNotInterested(
        this.trackingId,
        this.generateListOfStatusIDs(),
        this.formGroup.get('notInterestedNotes').value as string,
        this.jobSearchStatus,
        this.availability,
        this.fromTextMessage
      )
      .subscribe(
        result => {
          console.log('Not Interested reasons saved successfully.');
        },
        error => {
          this.encounteredError = true;
        }
      );
  }

  generateListOfStatusIDs(): number[] {
    const statusIds: number[] = [];
    for (const reason of this.notInterestedReasons) {
      const camelCaseReason = this.convertToCamelCase(reason);
      switch (camelCaseReason) {
        case 'skillSetMismatch':
          if (this.formGroup.get('notInterestedReasons').get(camelCaseReason).value) {
            statusIds.push(NotInterestedReasons.NOT_INTERESTED_MISMATCHED_SKILL_SET);
          }
          break;
        case 'levelMismatch':
          if (this.formGroup.get('notInterestedReasons').get(camelCaseReason).value) {
            statusIds.push(NotInterestedReasons.NOT_INTERESTED_MISMATCHED_LEVEL);
          }
          break;
        case 'payRate':
          if (this.formGroup.get('notInterestedReasons').get(camelCaseReason).value) {
            statusIds.push(NotInterestedReasons.NOT_INTERESTED_PAY_RATE);
          }
          break;
        case 'location':
          if (this.formGroup.get('notInterestedReasons').get(camelCaseReason).value) {
            statusIds.push(NotInterestedReasons.NOT_INTERESTED_LOCATION);
          }
          break;
        case 'industry':
          if (this.formGroup.get('notInterestedReasons').get(camelCaseReason).value) {
            statusIds.push(NotInterestedReasons.NOT_INTERESTED_INDUSTRY);
          }
          break;
        case 'other':
          if (this.formGroup.get('notInterestedReasons').get(camelCaseReason).value) {
            statusIds.push(NotInterestedReasons.NOT_INTERESTED_OTHER);
          }
          break;
      }
    }
    return statusIds;
  }

  setJobSearchStatus(): void {
    const employmentAvailabilityType = this.availability.availabilityType;
    const notActivelyLooking = !!this.formGroup.get('notInterestedReasons').get('notActivelyLooking').value;
    const isExpiredOrUnavailable =
      employmentAvailabilityType === AvailabilityTypeId.EXPIRED_BY_SYSTEM ||
      employmentAvailabilityType === AvailabilityTypeId.UNAVAILABLE;

    if (notActivelyLooking || isExpiredOrUnavailable) {
      this.jobSearchStatus = JobSearchStatusEnum.NOT_LOOKING;
    } else if (employmentAvailabilityType === AvailabilityTypeId.AVAILABLE_NOW) {
      this.jobSearchStatus = JobSearchStatusEnum.ACTIVELY_LOOKING;
    } else {
      this.jobSearchStatus = JobSearchStatusEnum.OPEN_TO_NEW_OPPORTUNITIES;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onConfirm(): void {
    this.submit();
    this.dialogRef.close();
  }

  submit(): void {
    this.saveAsNotInterested();
  }
}
